/**
 * ESC/POS over Web Bluetooth (Android Chrome, Bluetooth LE printers). Slower than USB (≈ 2–4 s a
 * receipt — design note of T07-25) so USB / LAN are recommended; kept for printers without a
 * cable. The GATT service UUIDs below are the ones the common thermal printers expose.
 */
import { DRAWER_KICK, chunks } from "./escpos";
import {
  navigatorHardware,
  type BluetoothDeviceLike,
  type BluetoothLike,
  type GattCharacteristicLike,
} from "./hardware";
import { PrinterError, type PrinterDriver, type PrinterStatus } from "./types";

export const BLE_PRINTER_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb", // most Xprinter / generic ESC/POS
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2", // many OEM (char bef8d6c9-…)
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // Microchip ISSC transparent UART
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000fee7-0000-1000-8000-00805f9b34fb",
];

/** LE packets: stay under the smallest common MTU payload. */
const BLE_CHUNK = 180;

export class WebBluetoothPrinter implements PrinterDriver {
  readonly kind = "bluetooth" as const;
  private device: BluetoothDeviceLike | null = null;
  private characteristic: GattCharacteristicLike | null = null;
  private readonly bluetooth: () => BluetoothLike | undefined;

  constructor(bluetooth: () => BluetoothLike | undefined = () => navigatorHardware().bluetooth) {
    this.bluetooth = bluetooth;
  }

  supported(): boolean {
    return this.bluetooth() !== undefined;
  }

  async connect(): Promise<PrinterStatus> {
    const bluetooth = this.bluetooth();
    if (!bluetooth) {
      throw new PrinterError("not_supported");
    }
    try {
      this.device = await bluetooth.requestDevice({
        filters: BLE_PRINTER_SERVICES.map((service) => ({ services: [service] })),
        optionalServices: BLE_PRINTER_SERVICES,
      });
    } catch (cause) {
      throw new PrinterError("not_found", cause instanceof Error ? cause.message : undefined);
    }
    this.device.addEventListener?.("gattserverdisconnected", () => {
      this.characteristic = null;
    });
    await this.writable();
    return this.status();
  }

  private async writable(): Promise<GattCharacteristicLike> {
    if (this.characteristic && this.device?.gatt?.connected) {
      return this.characteristic;
    }
    if (!this.device) {
      const known = (await this.bluetooth()?.getDevices?.()) ?? [];
      this.device = known[0] ?? null;
    }
    const gatt = this.device?.gatt;
    if (!gatt) {
      throw new PrinterError("not_found");
    }
    try {
      const server = gatt.connected ? gatt : await gatt.connect();
      for (const service of await server.getPrimaryServices()) {
        for (const characteristic of await service.getCharacteristics()) {
          if (characteristic.properties.writeWithoutResponse || characteristic.properties.write) {
            this.characteristic = characteristic;
            return characteristic;
          }
        }
      }
    } catch (cause) {
      throw new PrinterError("disconnected", cause instanceof Error ? cause.message : undefined);
    }
    throw new PrinterError("not_found", "no writable characteristic");
  }

  async print(bytes: Uint8Array): Promise<void> {
    const characteristic = await this.writable();
    for (const part of chunks(bytes, BLE_CHUNK)) {
      try {
        if (
          characteristic.properties.writeWithoutResponse &&
          characteristic.writeValueWithoutResponse
        ) {
          await characteristic.writeValueWithoutResponse(part);
        } else if (characteristic.writeValueWithResponse) {
          await characteristic.writeValueWithResponse(part);
        } else if (characteristic.writeValue) {
          await characteristic.writeValue(part);
        } else {
          throw new PrinterError("write_failed");
        }
      } catch (cause) {
        this.characteristic = null;
        if (cause instanceof PrinterError) {
          throw cause;
        }
        throw new PrinterError("disconnected", cause instanceof Error ? cause.message : undefined);
      }
    }
  }

  async openDrawer(): Promise<void> {
    await this.print(DRAWER_KICK);
  }

  async status(): Promise<PrinterStatus> {
    const connected = Boolean(this.device?.gatt?.connected);
    return {
      connected,
      name: this.device?.name ?? null,
      error: this.device ? (connected ? null : "disconnected") : "not_found",
    };
  }

  async disconnect(): Promise<void> {
    this.device?.gatt?.disconnect();
    this.characteristic = null;
    this.device = null;
  }
}
